import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { LabelFilterEintrag, LabelKandidaten } from "@kundeninfo/api-client";
import { useBestaetigung } from "@kundeninfo/ui";
import { Abschnitt, Auswahlmenue, Chip, Eingabe, Hinweis } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import type { LabelFilterClient } from "./fremdClient.js";
import "./LabelFilter.css";

/**
 * AUSGESCHLOSSENE LABELS (Spec Tickets, Nachtrag 1g, F156; Entscheide 232–234, Fragen 237–239): der Label-Filter einer
 * GitLab-Instanz im Detail der Schnittstellen-Ansicht, nur für ADMIN (die Ansicht hängt ihn nur dann ein; der Server
 * prüft ohnehin). Ein eigener `Abschnitt` neben weiteren des Details (etwa dem Hilfetext aus 1g-hilfe), ohne
 * Abhängigkeit zu ihnen. Block `lf__`.
 *
 * Chips wie in GitLab (233): `Chip farbe` mit der GitLab-Farbe — die Schrift leitet me-chip lesbar ab (Entscheid 41
 * von me-design, Frage 237), `entfernbar` mit ×. Hinzufügen über das `Auswahlmenue` mit Suchfeld ab 8 Labels; der
 * Farbpunkt ist ein SVG-Kreis als `data-bild` (sonst zeigte me-auswahlmenue Initialen in einer Nutzerfarbe). Ist die
 * Labelliste nicht ladbar oder unvollständig, gibt es zusätzlich die Eingabe von Hand. Jede Änderung erst nach
 * gestapelter Bestätigung (Hausregel „Bestätigung vor Datenänderung"); gespeichert wird als Unterschied (`erwartet` =
 * der gezeigte Stand), so überschreiben zwei ADMIN einander nicht (F155). Kollidiert die Änderung mit einer
 * gleichzeitigen (409 `LABELFILTER_GESPERRT`), steht die Meldung des Servers und der Stand wird neu geladen. Bei
 * unvollständiger Liste steht der `hinweis` des Servers (sonst `LISTE_UNVOLLSTAENDIG`).
 */
type Meldung = { art: "ok" | "fehler"; text: string };
type Label = { name: string; farbe: string | null };

/** Vergleichsschlüssel — NACHGEBILDET aus `labelSchluessel` in `@kundeninfo/domain` (web darf domain nicht laden,
 *  scripts/check-boundaries.mjs). Hier dient er nur dem Ausblenden schon ausgeschlossener; der Server vergleicht selbst. */
const schluessel = (name: string): string => name.trim().toLowerCase();

/** Farbpunkt einer Option (me-auswahlmenue `data-bild`): ein Kreis in der Label-Farbe. */
export function farbpunkt(farbe: string): string {
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><circle cx="8" cy="8" r="8" fill="${farbe}"/></svg>`)}`;
}

export const LISTE_FEHLT = "Die Labelliste aus GitLab ist gerade nicht ladbar — Namen von Hand eingeben.";
export const LISTE_UNVOLLSTAENDIG = "Die Labelliste ist unvollständig — fehlt ein Label, Namen von Hand eingeben.";

function fehlerText(f: unknown, sonst: string): string {
  return f instanceof ApiError ? (f.fields?.[0]?.message ?? f.message) : sonst;
}

export function LabelFilterAbschnitt(p: { client: LabelFilterClient; instanz: string; name: string }): ReactElement {
  const [items, setItems] = useState<LabelFilterEintrag[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [kandidaten, setKandidaten] = useState<LabelKandidaten | null>(null);
  const [kandidatenFehler, setKandidatenFehler] = useState(false);
  const [meldung, setMeldung] = useState<Meldung | null>(null);
  const [frei, setFrei] = useState("");
  const [freiFehler, setFreiFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [offen, setOffen] = useState(true);
  const { bestaetigen, bestaetigung } = useBestaetigung();
  /** Laufende Nummer des Ladens — eine Antwort zu einer anderen Instanz oder nach dem Aushängen wird verworfen. */
  const ladeNr = useRef(0);
  /** Hängt der Abschnitt noch? Das Neuladen nach 409 schreibt nur dann. */
  const haengt = useRef(true);
  useEffect(() => { haengt.current = true; return () => { haengt.current = false; }; }, []);

  useEffect(() => {
    const nr = ++ladeNr.current;
    setItems(null); setFehler(null); setKandidaten(null); setKandidatenFehler(false); setMeldung(null);
    void p.client.getLabelFilter("GITLAB", p.instanz).then(
      (r) => { if (nr === ladeNr.current) setItems(r.items); },
      (f: unknown) => {
        if (nr === ladeNr.current) setFehler(fehlerText(f, "Die ausgeschlossenen Labels konnten nicht geladen werden."));
      });
    void p.client.listLabelKandidaten("GITLAB", p.instanz).then(
      (r) => { if (nr === ladeNr.current) setKandidaten(r); },
      () => { if (nr === ladeNr.current) setKandidatenFehler(true); });
    return () => { ladeNr.current += 1; };
  }, [p.client, p.instanz]);

  /** Die Farbe eines Labels: aus der geladenen Labelliste (aktuell in GitLab), sonst die gespeicherte. */
  const farbeVon = (e: Label): string | null =>
    kandidaten?.items.find((k) => schluessel(k.name) === schluessel(e.name))?.farbe ?? e.farbe;

  async function speichern(vorher: LabelFilterEintrag[], neu: Label[], ok: (entfernt: number) => string): Promise<boolean> {
    setLaeuft(true); setMeldung(null);
    try {
      const r = await p.client.setLabelFilter("GITLAB", p.instanz, neu, vorher.map((e) => e.name));
      setItems(r.items);
      setMeldung({ art: "ok", text: ok(r.entfernt) });
      return true;
    } catch (f) {
      const text = fehlerText(f, "Der Filter ließ sich nicht speichern.");
      if (f instanceof ApiError && f.status === 409) {
        // Gleichzeitige Änderung eines anderen ADMIN: den aktuellen Stand holen, damit die nächste Wahl auf ihm aufsetzt.
        setMeldung({ art: "fehler", text: `${text} Der Stand ist neu geladen.` });
        const nr = ladeNr.current;
        try {
          const r = await p.client.getLabelFilter("GITLAB", p.instanz);
          if (haengt.current && nr === ladeNr.current) setItems(r.items);
        } catch { /* Stand bleibt; die Meldung steht schon. */ }
      } else {
        setMeldung({ art: "fehler", text });
      }
      return false;
    } finally {
      setLaeuft(false);
    }
  }

  async function ausschliessen(name: string, farbe: string | null): Promise<boolean> {
    if (items === null) return false;
    const vorher = items;
    const ja = await bestaetigen({ titel: `Label „${name}“ ausschließen?`, bestaetigenText: "Ausschließen",
      text: `Vorgänge mit diesem Label verschwinden sofort aus den Fremdtickets aller Nutzer von ${p.name} und kommen `
        + "erst wieder, wenn das Label zugelassen ist." });
    if (!ja) return false;
    return await speichern(vorher, [...vorher.map((e) => ({ name: e.name, farbe: e.farbe })), { name, farbe }],
      (n) => (n === 0 ? `„${name}“ ausgeschlossen.`
        : `„${name}“ ausgeschlossen — ${n === 1 ? "1 Fremdticket" : `${n} Fremdtickets`} ausgeblendet.`));
  }

  async function zulassen(e: LabelFilterEintrag): Promise<void> {
    if (items === null) return;
    const vorher = items;
    const ja = await bestaetigen({ titel: `Label „${e.name}“ wieder zulassen?`, bestaetigenText: "Zulassen",
      text: "Die Vorgänge erscheinen mit dem nächsten Abgleich wieder als Fremdtickets." });
    if (!ja) return;
    await speichern(vorher, vorher.filter((x) => x !== e).map((x) => ({ name: x.name, farbe: x.farbe })),
      () => `„${e.name}“ wieder zugelassen — die Vorgänge kommen mit dem nächsten Abgleich.`);
  }

  /** Das Menü ist unkontrolliert: während der Bestätigung zeigt es die Wahl, danach wieder den Platzhalter. Das
   *  Attribut wird entfernt statt auf "" gesetzt — "" passte zu keiner Option (Warnung von me-auswahlmenue). */
  const auswahlRef = useRef<HTMLElement>(null);
  async function gewaehlt(wert: string): Promise<void> {
    const k = kandidaten?.items.find((x) => x.name === wert);
    if (k !== undefined) await ausschliessen(k.name, k.farbe);
    auswahlRef.current?.removeAttribute("wert");
  }

  async function vonHand(): Promise<void> {
    const name = frei.trim();
    if (name === "") { setFreiFehler("Bitte einen Namen eingeben."); return; }
    if ((items ?? []).some((e) => schluessel(e.name) === schluessel(name))) {
      setFreiFehler("Dieses Label ist schon ausgeschlossen."); return;
    }
    const k = kandidaten?.items.find((x) => schluessel(x.name) === schluessel(name));
    if (await ausschliessen(k?.name ?? name, k?.farbe ?? null)) setFrei("");
  }

  const ausgeschlossen = new Set((items ?? []).map((e) => schluessel(e.name)));
  const auswahl = (kandidaten?.items ?? []).filter((k) => !ausgeschlossen.has(schluessel(k.name)));
  const vonHandNoetig = kandidatenFehler || (kandidaten !== null && !kandidaten.vollstaendig);
  return (
    <>
      <Abschnitt className="lf" titel="Ausgeschlossene Labels" zaehler={items === null ? "" : items.length} offen={offen}
        onUmgeschaltet={(d) => setOffen(d.offen)}>
        <div className="lf__inhalt">
          <p className="lf__hilfe">
            Vorgänge mit einem dieser Labels erscheinen bei niemandem als Fremdticket. Hinzufügen wirkt sofort, Entfernen
            mit dem nächsten Abgleich.
          </p>
          {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
          {items === null && fehler === null && <p className="lf__leer">Lädt …</p>}
          {items !== null && items.length === 0 && <p className="lf__leer">Keine Labels ausgeschlossen.</p>}
          {items !== null && items.length > 0 && (
            <ul className="lf__chips" aria-label="Ausgeschlossene Labels">
              {items.map((e) => {
                const farbe = farbeVon(e);
                return (
                  <li key={schluessel(e.name)}>
                    <Chip entfernbar {...(farbe === null ? {} : { farbe })} onEntfernt={() => { void zulassen(e); }}>
                      {e.name}
                    </Chip>
                  </li>
                );
              })}
            </ul>
          )}
          {items !== null && (
            <Auswahlmenue className="lf__auswahl" beschriftung="Label hinzufügen" sucheAb={8} ref={auswahlRef}
              gesperrt={laeuft || auswahl.length === 0}
              platzhalter={kandidaten === null ? (kandidatenFehler ? "Labelliste nicht ladbar" : "Lädt …")
                : auswahl.length === 0 ? "Keine weiteren Labels" : "Label wählen …"}
              onGeaendert={(d) => { void gewaehlt(d.wert); }}>
              {auswahl.map((k) => (
                <option key={schluessel(k.name)} value={k.name} data-name={k.name}
                  {...(k.farbe === null ? {} : { "data-bild": farbpunkt(k.farbe) })}>{k.name}</option>
              ))}
            </Auswahlmenue>
          )}
          {items !== null && vonHandNoetig && (
            <>
              <Hinweis art="info">{kandidatenFehler ? LISTE_FEHLT : (kandidaten?.hinweis ?? LISTE_UNVOLLSTAENDIG)}</Hinweis>
              <div className="lf__frei">
                <Eingabe className="lf__eingabe" beschriftung="Label-Name" wert={frei} maxLength={255}
                  {...(freiFehler === null ? {} : { fehler: freiFehler })}
                  onGeaendert={(d) => { setFrei(d.wert); setFreiFehler(null); }} />
                <KiKnopf deaktiviert={laeuft} onClick={() => { void vonHand(); }}>Ausschließen …</KiKnopf>
              </div>
            </>
          )}
          {meldung !== null && <Hinweis art={meldung.art}>{meldung.text}</Hinweis>}
        </div>
      </Abschnitt>
      {bestaetigung}
    </>
  );
}
